/**
 * Selected POI highlight layer — circle ring + icon overlay.
 * Always visible (minzoom: 0) so selected POI is never hidden by zoom tiers.
 */

import type { Map as MapLibreMap } from "maplibre-gl";
import type { Point } from "geojson";
import {
  buildIconImageExpression,
  buildIconSizeExpression,
  SPONSOR_POSITION_CONFIG,
  TEMP_CLOSED_OPACITY_EXPR,
  type DynamicCategory,
} from "@wondermaps/maps";

const SOURCE_ID = "selected-poi";
const RING_LAYER_ID = "selected-poi-ring";
const ICON_LAYER_ID = "selected-poi-icon";

/** Base ring radius in pixels (matches default poi.circleRadius = 20) */
const BASE_RING_RADIUS = 22;

export interface SelectedPoiFeature {
  id: string;
  location: Point;
  categorySlug: string;
  categoryColor: string;
  sponsorColorKey?: string;
  sponsorPosition?: number;
  status?: string | null;
}

/** Build a circle-radius expression that scales with sponsor position */
function buildRingRadiusExpression(): unknown[] {
  return [
    "*",
    BASE_RING_RADIUS,
    [
      "case",
      ["==", ["get", "sponsorPosition"], 1], SPONSOR_POSITION_CONFIG[1].sizeScale,
      ["==", ["get", "sponsorPosition"], 2], SPONSOR_POSITION_CONFIG[2].sizeScale,
      ["has", "sponsorPosition"], SPONSOR_POSITION_CONFIG[3].sizeScale,
      1,
    ],
  ];
}

function makeFeatureCollection(feature: SelectedPoiFeature | null): GeoJSON.FeatureCollection {
  if (!feature) {
    return { type: "FeatureCollection", features: [] };
  }
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: feature.location,
        properties: {
          id: feature.id,
          categorySlug: feature.categorySlug,
          categoryColor: feature.categoryColor,
          ...(feature.sponsorColorKey && { sponsorColorKey: feature.sponsorColorKey }),
          ...(feature.sponsorPosition && { sponsorPosition: feature.sponsorPosition }),
          ...(feature.status && { status: feature.status }),
        },
      },
    ],
  };
}

/** Add source + layers to map. Call once after POI layers are added. */
export function addSelectedPoiLayers(
  map: MapLibreMap,
  categories: DynamicCategory[],
  iconScale: number,
): void {
  if (map.getSource(SOURCE_ID)) return;

  map.addSource(SOURCE_ID, {
    type: "geojson",
    data: { type: "FeatureCollection", features: [] },
  });

  // Circle ring layer — stroke only, transparent fill
  map.addLayer({
    id: RING_LAYER_ID,
    type: "circle",
    source: SOURCE_ID,
    minzoom: 0,
    paint: {
      "circle-radius": buildRingRadiusExpression() as unknown as number,
      "circle-color": "transparent",
      "circle-stroke-width": 3,
      "circle-stroke-color": ["get", "categoryColor"] as unknown as string,
      "circle-stroke-opacity": 0.8,
    },
  });

  // Symbol icon layer — always on top, ignores placement
  const iconImage = buildIconImageExpression("categorySlug", categories);
  const iconSizeExpr = buildIconSizeExpression(iconScale);

  map.addLayer({
    id: ICON_LAYER_ID,
    type: "symbol",
    source: SOURCE_ID,
    minzoom: 0,
    layout: {
      "icon-image": iconImage as unknown as string,
      "icon-size": iconSizeExpr as unknown as number,
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
    },
    paint: {
      "icon-opacity": TEMP_CLOSED_OPACITY_EXPR as unknown as number,
      "icon-color": ["get", "categoryColor"] as unknown as string,
    },
  });
}

/** Update the selected POI feature (or clear with null). */
export function updateSelectedPoi(
  map: MapLibreMap,
  feature: SelectedPoiFeature | null,
): void {
  const source = map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource | undefined;
  if (!source) return;
  source.setData(makeFeatureCollection(feature));
}

/** Remove source + layers on cleanup. */
export function removeSelectedPoiLayers(map: MapLibreMap): void {
  if (map.getLayer(ICON_LAYER_ID)) map.removeLayer(ICON_LAYER_ID);
  if (map.getLayer(RING_LAYER_ID)) map.removeLayer(RING_LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
}
